import { useEffect, useState } from 'react';
import { ChevronDown, Lock, Unlock } from 'lucide-react';
import { boton } from './botones';

/** El evento con el que la tarjeta de Estado de la ficha abre una sección y la trae a la vista. */
const EVENTO_ABRIR_SECCION = 'atlas:abrir-seccion-de-ficha';

/**
 * Abre la sección `id` (si estaba plegada) y la desplaza a la vista. Lo usa la lista de
 * "lo que falta" de la ficha: tocar "Falta el precio" lleva al precio
 * (ADR-2026-10-06-la-ficha-ocupa-la-pantalla).
 */
export function irASeccion(id: string) {
  window.dispatchEvent(new CustomEvent(EVENTO_ABRIR_SECCION, { detail: id }));
  // Un cuadro después: si estaba plegada, primero se tiene que abrir para medir bien.
  requestAnimationFrame(() =>
    document.getElementById(`seccion-${id}`)?.scrollIntoView({ behavior: 'smooth', block: 'start' }),
  );
}

/**
 * La card plegable de la ficha de un producto: ícono en un chip, título,
 * resumen cuando está cerrada y aviso en ámbar cuando le falta algo obligatorio.
 *
 * Vivía adentro de `FichaDeProducto.tsx`. Se extrajo cuando el editor de un post
 * del calendario de redes necesitó exactamente la misma card
 * (ADR-2026-09-15-el-post-se-edita-al-lado-del-calendario): copiarla era la
 * segunda versión de lo mismo, o sea el punto donde las dos empiezan a
 * divergir — el mismo motivo por el que se extrajo `ModalConfirmar`.
 *
 * El chip del ícono es del azul suave de Atlas en TODAS las secciones: un solo tono,
 * para que el color siga queriendo decir estado (el ámbar del aviso) y no "qué
 * sección es" (ADR-2026-10-06-la-ficha-ocupa-la-pantalla).
 */
export function Seccion({
  id,
  titulo,
  icono,
  resumen,
  aviso,
  plegable = true,
  abiertaPorDefecto,
  candado,
  children,
}: {
  /** Para llegar a ella desde afuera con `irASeccion(id)`. */
  id?: string;
  titulo: string;
  icono: React.ReactNode;
  resumen?: string;
  /** Texto en ámbar: algo obligatorio de esta sección todavía falta. */
  aviso?: string | null;
  plegable?: boolean;
  abiertaPorDefecto?: boolean;
  /**
   * La sección de un producto que ya está en la tienda
   * (ADR-2026-10-24-las-secciones-de-un-producto-de-la-tienda-arrancan-trabadas). Trabada, se ve gris y
   * cerrada con lo que hoy está publicado, y sólo ofrece "Editar en la tienda"; destrabada, es la
   * sección de siempre con una línea que dice que se actualiza y deja volver a trabarla.
   * Sin esto, la sección de siempre.
   */
  candado?: {
    trabada: boolean;
    /** Lo que hoy está publicado, en una línea. */
    enLaTienda: string;
    /** Cuántos cambios de la ficha quedan sin mandar por estar trabada. */
    sinMandar: number;
    onCambiar: (trabada: boolean) => void;
  };
  children: React.ReactNode;
}) {
  const [abierta, setAbierta] = useState(abiertaPorDefecto ?? !plegable);
  useEffect(() => {
    if (!id) return;
    const abrir = (e: Event) => {
      if ((e as CustomEvent<string>).detail === id) setAbierta(true);
    };
    window.addEventListener(EVENTO_ABRIR_SECCION, abrir);
    return () => window.removeEventListener(EVENTO_ABRIR_SECCION, abrir);
  }, [id]);
  // Al destrabar se abre: es para editarla.
  const trabada = candado?.trabada === true;
  useEffect(() => {
    if (candado && !trabada) setAbierta(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trabada]);

  if (candado && trabada) {
    return (
      <section
        id={id ? `seccion-${id}` : undefined}
        aria-label={`${titulo}: no se toca en la tienda`}
        className="scroll-mt-24 overflow-hidden rounded-2xl border border-neutral-200 bg-[#FAFAFA]"
      >
        <div className="flex items-center gap-3 p-4 sm:p-5">
          <span className="grid h-9 w-9 shrink-0 place-items-center rounded-xl bg-[#EFEFEF] text-neutral-400">
            <Lock className="w-4 h-4" aria-hidden="true" />
          </span>
          <span className="min-w-0 flex-1">
            <span className="block text-sm font-medium text-neutral-500">{titulo}</span>
            <span className="mt-0.5 block text-xs text-neutral-400">No se toca · queda como en tu tienda</span>
            <span className="mt-0.5 block truncate text-xs text-brand-black">{candado.enLaTienda}</span>
            {candado.sinMandar > 0 && (
              <span className="mt-0.5 block text-[11px] text-neutral-400">
                {candado.sinMandar === 1
                  ? 'Tenés 1 cambio acá que no se manda.'
                  : `Tenés ${candado.sinMandar} cambios acá que no se mandan.`}
              </span>
            )}
          </span>
          <button
            type="button"
            onClick={() => candado.onCambiar(false)}
            aria-label={`Editar ${titulo.toLowerCase()} en la tienda`}
            className={boton('neutro', { chico: true, extra: 'shrink-0 bg-white ring-1 ring-neutral-200' })}
          >
            <Unlock className="h-3.5 w-3.5" aria-hidden="true" />
            <span className="sm:hidden">Editar</span>
            <span className="hidden sm:inline">Editar en la tienda</span>
          </button>
        </div>
      </section>
    );
  }

  const contenido = (
    <div className="space-y-3 px-4 pb-4 sm:px-5 sm:pb-5">
      {candado && (
        <div className="flex items-center justify-between gap-3 rounded-xl bg-[rgb(var(--brand-primary-rgb)_/_0.049)] py-2 px-3">
          <span className="flex min-w-0 items-center gap-2 text-xs text-brand-primary">
            <Unlock className="h-3.5 w-3.5 shrink-0" aria-hidden="true" />
            Se actualiza en tu tienda al publicar
          </span>
          <button
            type="button"
            onClick={() => candado.onCambiar(true)}
            className="flex shrink-0 items-center gap-1 text-xs font-medium text-brand-primary hover:underline"
          >
            <Lock className="h-3.5 w-3.5" aria-hidden="true" />
            Trabar
          </button>
        </div>
      )}
      {children}
    </div>
  );

  const cabecera = (
    <>
      <span
        className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${
          aviso ? 'bg-amber-50 text-amber-600' : 'bg-[rgb(var(--brand-primary-rgb)_/_0.07)] text-brand-primary'
        }`}
      >
        {icono}
      </span>
      <span className="min-w-0 flex-1">
        <span className="block text-sm font-medium text-brand-black">{titulo}</span>
        {(aviso || (!abierta && resumen)) && (
          <span
            className={`mt-0.5 block truncate text-xs ${aviso ? 'text-amber-700' : 'text-neutral-400'}`}
          >
            {aviso ?? resumen}
          </span>
        )}
      </span>
    </>
  );

  return (
    <section
      id={id ? `seccion-${id}` : undefined}
      className={`scroll-mt-24 overflow-hidden rounded-2xl border bg-surface-card shadow-sm ${
        aviso ? 'border-amber-200' : 'border-neutral-200'
      }`}
    >
      {plegable ? (
        <button
          type="button"
          onClick={() => setAbierta((v) => !v)}
          aria-expanded={abierta}
          className="flex w-full items-center gap-3 p-4 text-left transition-colors hover:bg-neutral-50/60 sm:p-5"
        >
          {cabecera}
          <ChevronDown
            className={`w-4 h-4 shrink-0 text-neutral-400 transition-transform ${abierta ? 'rotate-180' : ''}`}
          />
        </button>
      ) : (
        <div className="flex items-center gap-3 p-4 sm:p-5">{cabecera}</div>
      )}
      {abierta && contenido}
    </section>
  );
}

export default Seccion;
